import { Alert, Box, Button, Text, VStack } from '@chakra-ui/react'
import { Handle, NodeProps, Position } from '@xyflow/react'
import React from 'react'

import {
  CallDistributionNodeData,
  CallDistributionOption,
} from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { nodeAddOptionButtonStyles, NodeInput } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useCallDistributionNode } from './hooks/useCallDistributionNode'

export interface CallDistributionNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void

  showDeleteButton?: boolean
}

const CallDistributionNodeComponent: React.FC<CallDistributionNodeProps> = ({
  id,
  data,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as CallDistributionNodeData & Record<string, unknown>
  const {
    timeout,
    currentOptions,
    weightInputs,
    totalWeight,
    isWeightValid,
    handleTimeoutChange,
    handleTimeoutBlur,
    handleWeightChange,
    handleWeightBlur,
    handleOptionsChange,
    hasData,
  } = useCallDistributionNode(id, nodeData)
  const t = useSwitchboardT()

  const handleAddOption = React.useCallback(() => {
    // Default weight to 1 (minimum allowed) or distribute evenly if options exist
    const defaultWeight =
      currentOptions.length > 0
        ? Math.max(1, Math.floor(100 / (currentOptions.length + 1)))
        : 50
    const newOption: CallDistributionOption = {
      weight: defaultWeight,
    }
    handleOptionsChange([...currentOptions, newOption])
  }, [currentOptions, handleOptionsChange])

  const handleRemoveOption = React.useCallback(
    (index: number) => {
      const updatedOptions = currentOptions.filter((_, i) => i !== index)
      handleOptionsChange(updatedOptions)
    },
    [currentOptions, handleOptionsChange],
  )

  return (
    <BaseNode
      label={text(
        t,
        'switchboard.nodeLabels.call_distribution',
        'CALL DISTRIBUTION',
      )}
      showPlaceholder={!hasData}
      sourceHandlePosition={null}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
    >
      <VStack align="stretch" gap={2}>
        <NodeInput
          label={text(t, 'switchboard.nodes.transfer.timeout', 'Timeout')}
          type="number"
          value={timeout}
          onChange={(e) => handleTimeoutChange(e.target.value)}
          onBlur={handleTimeoutBlur}
          placeholder="20"
          min={2}
          max={40}
        />

        {/* Add Option Button */}
        <Box>
          <Button
            className="nodrag"
            {...nodeAddOptionButtonStyles}
            onClick={(e) => {
              e.stopPropagation()
              handleAddOption()
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {text(t, 'switchboard.nodes.common.addOption', '+ Add Option')}
          </Button>
        </Box>

        {/* Options List */}
        {currentOptions.length > 0 && (
          <Box>
            <VStack align="stretch" gap={2}>
              {currentOptions.map((option, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  position="relative"
                  width="100%"
                >
                  {/* Handle positioned at node edge - next to the input */}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`option-${index}`}
                    isConnectableEnd={false}
                    style={{
                      position: 'absolute',
                      right: '-24px', // Card.Body padding (16px for p={4}) + half handle width (8px) = 24px
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      // Colors handled by CSS for hover/highlight support
                    }}
                  />
                  {/* Remove button */}
                  <Button
                    className="nodrag"
                    size="xs"
                    variant="ghost"
                    colorPalette="neutral"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveOption(index)
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    minWidth="auto"
                    width="20px"
                    height="20px"
                    padding={0}
                  >
                    ×
                  </Button>
                  {/* Weight input */}
                  <Box flex={1}>
                    <NodeInput
                      label={text(
                        t,
                        'switchboard.nodes.callDistribution.weight',
                        'Weight',
                      )}
                      type="number"
                      value={weightInputs[index] || ''}
                      onChange={(e) =>
                        handleWeightChange(index, e.target.value)
                      }
                      onBlur={() => handleWeightBlur(index)}
                      placeholder="50"
                      min={1}
                      max={100}
                      compact
                    />
                  </Box>
                  {/* Percentage sign */}
                  <Text fontSize="sm" color="fg.default">
                    %
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* Weight Validation Warning - show if total != 100% (matches Legacy) */}
        {currentOptions.length > 0 && !isWeightValid && (
          <Alert.Root status="warning" variant="subtle">
            <Alert.Indicator />
            <Alert.Title>
              {text(
                t,
                'switchboard.nodes.callDistribution.weightWarning',
                "Warning! Total weight percentage should be 100. Now it's {{total}}",
                { total: String(totalWeight) },
              )}
            </Alert.Title>
          </Alert.Root>
        )}
      </VStack>
    </BaseNode>
  )
}

export const CallDistributionNode = React.memo(
  CallDistributionNodeComponent,
  nodePropsAreEqual,
)
